// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — two corrections to MapLibre's vertical-perspective globe, so the
 * city close-up (cityCloseup.ts) draws every tile and its
 * terrain at street zoom. Found and measured 2026-10-17
 * (docs/AYNI_XR_MAPLIBRE_SPHERE_TERRAIN_PLAN.md):
 *
 * 1. **Near plane.** MapLibre puts the sphere's near clipping plane 0.5 px
 *    from the camera (and the far plane two globe diameters away), so depth
 *    precision halves with every zoom level. From about zoom 12.5 the
 *    terrain tiles' "skirts" (walls about 2πR/2^z/5 deep under every tile
 *    edge, not scaled by the exaggeration) poke through the ground as thin
 *    vertical pillars. The near plane here is a quarter of the camera's
 *    clearance over the ground under it.
 * 2. **Frustum.** MapLibre builds the view's frustum from the near plane's
 *    corners, less than a metre apart at city zoom, so its side planes come
 *    out tilted (60–370 m off at zoom 15) and on-screen tiles are culled:
 *    the "missing tiles" at the near edge, and nothing at all at zoom 17.
 *    The frustum here is built from the camera position and the rays
 *    through the four screen corners, all in double precision.
 *
 * On the PC's GPU (Intel, D3D11), flat terrain over Barcelona, zoom 13–17,
 * tilt 0–70°, three directions, terrain on and off: stock MapLibre leaves up
 * to 31 % of the screen undrawn from zoom 15 and all of it at 17; with both
 * corrections, nothing. SwiftShader (headless software GL) cannot judge
 * this: its atan/exp draw the map ~250 m off at zoom 14.
 *
 * They reach into MapLibre's private transform (`_verticalPerspectiveTransform`,
 * `_calcMatrices`, `_helper`, `_cameraPosition`, `getRayDirectionFromPixel`;
 * since MapLibre 6 the transform is `map._camera.transform`, maplibreInternals.ts),
 * so they are installed only on the version they were written against
 * ({@link PATCHED_MAPLIBRE_VERSION}; a test fails when the package moves) and
 * only from {@link SPHERE_FIX_FROM_ZOOM}: below it the stock code runs.
 */

import { mapTransform, setCameraUpdateHook } from './maplibreInternals'

/** The MapLibre release these corrections were written and measured against. */
export const PATCHED_MAPLIBRE_VERSION = '6.15.0'

/** Below this zoom the stock near plane and frustum are used (they are fine far out). */
export const SPHERE_FIX_FROM_ZOOM = 9

/**
 * The near plane as a share of the camera's clearance over the ground under
 * it. Measured: a quarter keeps the pillars away to zoom 17+; MapLibre's
 * flat-map rule (screen height / 50, 16 px) lets them back at 16–17.
 */
export const NEAR_PLANE_SHARE = 0.25

/**
 * The far plane's distance beyond the view centre, in globe radii: the
 * globe's far side (2) plus the earth layer's atmosphere shell
 * (`ATMOSPHERE_RADIUS_FACTOR` ≈ 1.016, on both sides) with room to spare.
 */
export const FAR_PLANE_RADII = 2.2

/** Earth's radius as MapLibre uses it, in metres. */
const EARTH_RADIUS_M = 6371008.8

type Vec3 = [number, number, number]
type Plane = [number, number, number, number]

/** A frustum in the shape MapLibre's bounding volumes test against. */
export interface SphereFrustum {
  planes: Plane[]
  points: [number, number, number, number][]
}

const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
const cross = (a: Vec3, b: Vec3): Vec3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
const normalize = (a: Vec3): Vec3 => {
  const l = Math.hypot(a[0], a[1], a[2])
  return [a[0] / l, a[1] / l, a[2] / l]
}

/**
 * The near plane distance in pixels: {@link NEAR_PLANE_SHARE} of the
 * camera's height over the ground under it. On the sphere MapLibre stands
 * the camera `cameraToCenterDistance` from the view's centre point taken at
 * sea level (the terrain's height there is ignored), `pitch` degrees from
 * straight down, so its height over the sea is
 * `cameraToCenterDistance · cos(pitch)`; `groundRisePx` is the ground's
 * height over the sea under the camera. Never under MapLibre's own 0.5 px.
 */
export function sphereNearZ(cameraToCenterDistance: number, pitchDegrees: number, groundRisePx = 0): number {
  const height = cameraToCenterDistance * Math.cos(pitchDegrees * Math.PI / 180) - Math.max(0, groundRisePx)
  return Math.max(0.5, height * NEAR_PLANE_SHARE)
}

/**
 * A frustum from the camera position and the unit rays through the screen's
 * corners (top-left, top-right, bottom-right, bottom-left) and centre, on
 * the unit sphere. The far plane is past the globe's far side; the horizon
 * plane MapLibre tests alongside does the real far culling.
 */
export function frustumFromRays(camera: Vec3, corners: [Vec3, Vec3, Vec3, Vec3], forward: Vec3): SphereFrustum {
  const far = Math.hypot(camera[0], camera[1], camera[2]) + 1
  const side = (a: Vec3, b: Vec3): Plane => {
    let n = normalize(cross(a, b))
    if (dot(n, forward) < 0) n = [-n[0], -n[1], -n[2]]
    return [n[0], n[1], n[2], -dot(n, camera)]
  }
  const at = (r: Vec3, d: number): [number, number, number, number] => [camera[0] + r[0] * d, camera[1] + r[1] * d, camera[2] + r[2] * d, 1]
  const [tl, tr, br, bl] = corners
  return {
    // MapLibre's order: near, far, left, right, bottom, top.
    planes: [
      [forward[0], forward[1], forward[2], -dot(forward, camera)],
      [-forward[0], -forward[1], -forward[2], dot(forward, camera) + far],
      side(bl, tl),
      side(tr, br),
      side(br, bl),
      side(tl, tr),
    ],
    points: [...corners.map((r) => at(r, far)), ...corners.map((r) => at(r, 1e-9))],
  }
}

/** The pieces of MapLibre's vertical-perspective transform the corrections use. */
interface PrivateSphereTransform {
  _calcMatrices(): void
  _helper: { _width: number; _height: number; _nearZ: number; _farZ: number; _autoCalculateNearFarZ: boolean }
  _cameraPosition: ArrayLike<number>
  /** Rebuilt as a new array by every `_calcMatrices`; what `getRayDirectionFromPixel` unprojects with (`…NoCorrectionInverted` before MapLibre 6). */
  _globeViewProjMatrixF64Inverted?: object
  getRayDirectionFromPixel(p: { x: number; y: number }): ArrayLike<number>
  getCameraFrustum(): unknown
  cameraToCenterDistance: number
  pitch: number
  zoom: number
  worldSize: number
  elevation: number
  width: number
  height: number
  center: { lng: number; lat: number }
}

/** The ground's height in metres (with the terrain's exaggeration) at a point, or null. */
export type GroundProbe = (lng: number, lat: number) => number | null

interface MapLike {
  /** `_camera.transform` since MapLibre 6, `transform` before (maplibreInternals.ts). */
  _camera?: { transform?: unknown }
  transform?: unknown
  terrain?: { getElevationForLngLatZoom(lngLat: { lng: number; lat: number; wrap(): unknown }, zoom: number): number } | null
  getZoom(): number
  getTerrain?(): { exaggeration?: number } | null
  /** MapLibre 5 took the hook as this property; 6 through `setTransformCameraUpdate` (maplibreInternals.ts). */
  transformCameraUpdate?: ((next: CameraState) => { pitch?: number; zoom?: number }) | null
  setTransformCameraUpdate?(hook: ((next: CameraState) => { pitch?: number; zoom?: number }) | null): void
  getCenter?(): { lng: number; lat: number }
  getPitch?(): number
  getBearing?(): number
  jumpTo?(options: { pitch?: number; zoom?: number }): unknown
  on?(type: 'idle', listener: () => void): unknown
}

/** The camera MapLibre is about to apply (`transformCameraUpdate`'s argument, the parts used here). */
interface CameraState {
  center: { lng: number; lat: number }
  zoom: number
  pitch: number
  bearing: number
}

const probes = new WeakMap<object, GroundProbe>()
const clamped = new WeakSet<object>()
let installed = false

/**
 * Keep `map`'s camera above the ground under it ({@link keepCameraAboveGround})
 * on every camera change from {@link SPHERE_FIX_FROM_ZOOM}, through MapLibre's
 * public `transformCameraUpdate`, and once more whenever the map goes idle:
 * the terrain under the camera may only just have loaded. The ground is
 * judged at `exaggerationAt` the camera's new zoom, the relief the app will
 * draw there: mid-move the terrain may still carry the whole globe's 5×.
 */
function installCameraAboveGround(map: MapLike, makeLngLat: (lng: number, lat: number) => { lng: number; lat: number; wrap(): unknown }, exaggerationAt: (zoom: number) => number): void {
  if (clamped.has(map) || !map.on || !map.jumpTo || !map.getCenter || !map.getPitch || !map.getBearing) return
  clamped.add(map)
  const c2c = () => mapTransform<{ cameraToCenterDistance?: number }>(map)?.cameraToCenterDistance ?? 0
  const groundAt = (lng: number, lat: number, zoom: number): number => {
    const drawn = map.getTerrain?.()?.exaggeration
    const h = map.terrain?.getElevationForLngLatZoom(makeLngLat(lng, lat), zoom)
    if (h === undefined || !Number.isFinite(h) || !drawn) return 0
    return h / drawn * exaggerationAt(zoom)
  }
  const constrain = (next: CameraState) => (next.zoom >= SPHERE_FIX_FROM_ZOOM && map.terrain ? keepCameraAboveGround(next, c2c(), groundAt) : {})
  setCameraUpdateHook(map, constrain as never)
  map.on('idle', () => {
    const change = constrain({ center: map.getCenter!(), zoom: map.getZoom(), pitch: map.getPitch!(), bearing: map.getBearing!() })
    if (change.pitch !== undefined || change.zoom !== undefined) map.jumpTo!(change)
  })
}

/**
 * Install both corrections into MapLibre's vertical-perspective transform
 * (once per page: they patch its class) and register `map`'s terrain as the
 * ground under the camera, which the camera is kept above
 * (`exaggerationAt`: the terrain exaggeration the app draws at a zoom).
 * Returns false, changing nothing, on another MapLibre version or when the
 * private pieces are missing.
 */
export function installSphereCloseupFix(
  map: MapLike,
  version: string,
  makeLngLat: (lng: number, lat: number) => { lng: number; lat: number; wrap(): unknown },
  exaggerationAt: (zoom: number) => number = () => 1,
): boolean {
  if (version !== PATCHED_MAPLIBRE_VERSION) {
    console.warn(`[cityCloseup] MapLibre ${version}: sphere close-up corrections are for ${PATCHED_MAPLIBRE_VERSION}, not installed`)
    return false
  }
  const vp = mapTransform<{ _verticalPerspectiveTransform?: PrivateSphereTransform }>(map)?._verticalPerspectiveTransform
  const proto = vp && Object.getPrototypeOf(vp) as PrivateSphereTransform
  if (!vp || !proto || typeof proto._calcMatrices !== 'function' || typeof proto.getRayDirectionFromPixel !== 'function' || typeof proto.getCameraFrustum !== 'function' || !vp._helper) {
    console.warn('[cityCloseup] MapLibre transform not as expected: sphere close-up corrections not installed')
    return false
  }
  probes.set(vp._helper, (lng, lat) => {
    const terrain = map.terrain
    if (!terrain) return null
    const h = terrain.getElevationForLngLatZoom(makeLngLat(lng, lat), map.getZoom())
    return Number.isFinite(h) ? h : null
  })
  installCameraAboveGround(map, makeLngLat, exaggerationAt)
  if (installed) return true
  installed = true

  const stockCalc = proto._calcMatrices
  proto._calcMatrices = function (this: PrivateSphereTransform) {
    const h = this._helper
    if (!h?._width || !h._height || !h._autoCalculateNearFarZ || !(this.zoom >= SPHERE_FIX_FROM_ZOOM)) {
      stockCalc.call(this)
      return
    }
    // Our near plane, and a far plane past the far side of the atmosphere
    // shell (the earth layer's, ~1.016 R): seen from inside it, the shell's
    // far side is what the haze is drawn on, and MapLibre's far plane
    // (camera + 2 R) cut a sharp haze-free disc out of the middle of the view
    // once the depth was precise (2026-10-18). Depth precision hangs on the
    // near plane, so the extra distance costs nothing. The flag stays on for
    // everyone else (the helper is shared with the Mercator transform).
    const radiusPx = this.worldSize / (2 * Math.PI) / Math.cos(this.center.lat * Math.PI / 180)
    h._nearZ = sphereNearZ(this.cameraToCenterDistance, this.pitch, groundRisePx(this, radiusPx))
    h._farZ = this.cameraToCenterDistance + radiusPx * FAR_PLANE_RADII
    h._autoCalculateNearFarZ = false
    try {
      stockCalc.call(this)
    } finally {
      h._autoCalculateNearFarZ = true
    }
  }

  const stockFrustum = proto.getCameraFrustum
  // Keyed by the inverse matrix MapLibre rebuilds (a new array) on every
  // camera change: any change, bearing included, gives a new frustum.
  const cache = new WeakMap<object, SphereFrustum>()
  proto.getCameraFrustum = function (this: PrivateSphereTransform) {
    const matrix = this._globeViewProjMatrixF64Inverted
    if (!(this.zoom >= SPHERE_FIX_FROM_ZOOM) || !matrix) return stockFrustum.call(this)
    const hit = cache.get(matrix)
    if (hit) return hit
    const c = this._cameraPosition
    const ray = (x: number, y: number): Vec3 => { const r = this.getRayDirectionFromPixel({ x, y }); return [r[0], r[1], r[2]] }
    const W = this.width
    const H = this.height
    const frustum = frustumFromRays([c[0], c[1], c[2]], [ray(0, 0), ray(W, 0), ray(W, H), ray(0, H)], ray(W / 2, H / 2))
    cache.set(matrix, frustum)
    return frustum
  }
  return true
}

/**
 * The ground's height over the sea under the camera, in pixels, from the
 * last frame's camera position (it moves little between frames). 0 without
 * terrain or before the first frame.
 */
function groundRisePx(t: PrivateSphereTransform, radiusPx: number): number {
  const probe = probes.get(t._helper)
  const c = t._cameraPosition
  if (!probe || !c || !(Math.hypot(c[0], c[1], c[2]) > 0)) return 0
  const len = Math.hypot(c[0], c[1], c[2])
  const lat = Math.asin(c[1] / len) * 180 / Math.PI
  const lng = Math.atan2(c[0], c[2]) * 180 / Math.PI
  const ground = probe(lng, lat)
  if (ground === null) return 0
  return ground * radiusPx / EARTH_RADIUS_M
}

/**
 * The least height, in metres, the camera keeps over the ground under it.
 * On the sphere MapLibre measures the camera from the view centre at sea
 * level, so over hills a close, tilted camera ends up inside the terrain
 * (Tibidabo at zoom 16: 105 m up at 85°, the ground under it 366 m), and
 * MapLibre's own lift cannot help there: its `getCameraAltitude` gives
 * nothing on the globe (maplibre-gl-js #8719).
 */
export const CAMERA_GROUND_CLEARANCE_M = 100

/** Metres a screen pixel covers at the view centre on MapLibre's globe (512 px world tiles). */
export function metresPerPixel(zoom: number, lat: number): number {
  return 2 * Math.PI * EARTH_RADIUS_M * Math.cos(lat * Math.PI / 180) / (512 * 2 ** zoom)
}

/** Where on the ground the camera stands, for a view centre, tilt, bearing and its height looking straight down. */
export function cameraGroundPoint(lng: number, lat: number, pitchDegrees: number, bearingDegrees: number, straightDownHeightM: number): [number, number] {
  const d = straightDownHeightM * Math.sin(pitchDegrees * Math.PI / 180)
  const b = bearingDegrees * Math.PI / 180
  // Behind the centre, against the bearing.
  return [lng - d * Math.sin(b) / (111_320 * Math.cos(lat * Math.PI / 180)), lat - d * Math.cos(b) / 111_320]
}

/**
 * The tilt and zoom that keep the camera {@link CAMERA_GROUND_CLEARANCE_M}
 * over the ground under it: the tilt eased just enough, and if even looking
 * straight down is too low (a peak under the view), the zoom too. Only the
 * values that change are returned. `groundAt` gives the ground's height in
 * metres at a point (0 when unknown).
 */
export function keepCameraAboveGround(
  next: { center: { lng: number; lat: number }; zoom: number; pitch: number; bearing: number },
  cameraToCenterDistance: number,
  groundAt: (lng: number, lat: number, zoom: number) => number,
): { pitch?: number; zoom?: number } {
  const { lng, lat } = next.center
  const out: { pitch?: number; zoom?: number } = {}
  let zoom = next.zoom
  const height = (z: number) => cameraToCenterDistance * metresPerPixel(z, lat)
  const underCentre = Math.max(0, groundAt(lng, lat, zoom)) + CAMERA_GROUND_CLEARANCE_M
  if (height(zoom) < underCentre) {
    zoom = Math.log2(cameraToCenterDistance * 2 * Math.PI * EARTH_RADIUS_M * Math.cos(lat * Math.PI / 180) / (512 * underCentre))
    out.zoom = zoom
  }
  let pitch = next.pitch
  // Easing the tilt brings the camera nearer the centre, maybe over higher ground: look again.
  for (let i = 0; i < 4; i++) {
    const [cl, ca] = cameraGroundPoint(lng, lat, pitch, next.bearing, height(zoom))
    const need = Math.max(0, groundAt(cl, ca, zoom)) + CAMERA_GROUND_CLEARANCE_M
    const allowed = need >= height(zoom) ? 0 : Math.acos(need / height(zoom)) * 180 / Math.PI
    if (pitch <= allowed + 1e-6) break
    pitch = allowed
  }
  if (pitch < next.pitch - 1e-6) out.pitch = pitch
  return out
}
